/**
 * Shared UI primitives.
 *
 * Every screen gets three states, not one: loaded, loading (skeleton, never a
 * spinner, never a layout shift) and empty (designed, with a next action). A
 * brand-new garage sees nine empty screens on day one — that is the first
 * impression, so it is built here rather than left to each page.
 */

import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Check, Info, X } from "lucide-react";
import {
  Component,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DURATION, EASE, fadeUp, scaleIn, spring, staggerItem, staggerList } from "@/lib/motion";

export const cn = (...classes: unknown[]) => twMerge(clsx(classes));

/* --- Skeletons ---------------------------------------------------------- */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("skeleton h-4 w-full", className)} />;
}

export function SkeletonCard() {
  return (
    <div className="card space-y-3 p-4">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}

/** Matches the real table's row height so nothing jumps when data lands. */
export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="card divide-y divide-line">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

/* --- Empty state -------------------------------------------------------- */

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="flex flex-col items-center justify-center rounded-xl border border-dashed
                 border-line bg-elevated/40 px-6 py-14 text-center"
    >
      {Icon && (
        <div className="mb-4 rounded-2xl bg-brand/10 p-3.5">
          <Icon className="h-7 w-7 text-brand" />
        </div>
      )}
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-muted">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </motion.div>
  );
}

/* --- Error state -------------------------------------------------------- */

export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="card border-red-500/30 bg-red-500/5 p-6 text-center">
      <AlertTriangle className="mx-auto mb-3 h-6 w-6 text-red-500" />
      <h3 className="font-semibold text-ink">{title}</h3>
      {message && <p className="mt-1 text-sm text-muted">{message}</p>}
      {onRetry && (
        <button onClick={onRetry} className="btn-outline mt-4">
          Try again
        </button>
      )}
    </div>
  );
}

/* --- Route error boundary ------------------------------------------------ */

const CHUNK_RELOAD_KEY = "garageos.chunkReload";

/**
 * Catches a route that fails to render — most commonly a lazy `import()`
 * rejecting because the dev server (or a fresh deploy) has moved on since
 * this tab last loaded, so the chunk it's asking for by hash no longer
 * exists. `<Suspense>` only covers the *pending* state of a lazy import; a
 * *rejected* one throws during render and only an error boundary catches it
 * — without one, that throw unmounts the whole routed tree and the click
 * that triggered it just appears to do nothing.
 *
 * A stale chunk is fixed by reloading, not by re-rendering, so that's what
 * this does — once. The flag guards against a reload loop if the failure is
 * actually persistent rather than a stale chunk, so the second failure falls
 * through to the retry UI instead of refreshing forever.
 */
export class RouteErrorBoundary extends Component<
  { children: ReactNode; resetKey?: unknown },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    const looksStale = /dynamically imported module|failed to fetch|loading chunk/i.test(
      error.message,
    );
    if (looksStale && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      window.location.reload();
    }
  }

  /**
   * Without this, a crash on ONE page sticks forever: `render()` below
   * returns the fallback unconditionally once `state.error` is set, and
   * nothing ever unsets it — so navigating away to a perfectly healthy page
   * still shows the same "didn't load" card, and only a full reload (which
   * remounts everything, this boundary included) recovers. `resetKey` is
   * meant to be the current route; when it changes while we're showing the
   * fallback, give the new route an unpoisoned attempt at rendering.
   */
  componentDidUpdate(prevProps: { resetKey?: unknown }) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="grid min-h-full place-items-center p-6">
        <div className="w-full max-w-md">
          <ErrorState
            title="This page didn't load"
            message="Reloading almost always fixes it."
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }
}

/* --- Count-up ----------------------------------------------------------- */

/**
 * Figures count up rather than snapping. Deliberately capped at ~700ms —
 * long enough to read as motion, short enough that nobody waits for a number.
 */
export function CountUp({
  value,
  prefix = "",
  decimals = 0,
  className,
}: {
  value: number;
  prefix?: string;
  decimals?: number;
  className?: string;
}) {
  const [display, setDisplay] = useState(0);

  useMemo(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    const from = display;
    const duration = 700;

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (value - from) * eased);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <span className={className}>
      {prefix}
      {display.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
    </span>
  );
}

/* --- Toasts ------------------------------------------------------------- */

type Toast = { id: number; kind: "ok" | "error" | "info"; message: string };

const ToastContext = createContext<{
  push: (kind: Toast["kind"], message: string) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: Toast["kind"], message: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, message }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full
                      max-w-sm flex-col gap-2 px-4 sm:px-0">
        <AnimatePresence initial={false}>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: 16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24, scale: 0.97 }}
              transition={spring}
              className={cn(
                "pointer-events-auto flex items-start gap-3 rounded-xl border p-3.5 shadow-lift",
                toast.kind === "ok" && "border-emerald-500/30 bg-emerald-500/10",
                toast.kind === "error" && "border-red-500/30 bg-red-500/10",
                toast.kind === "info" && "border-line bg-elevated",
              )}
            >
              {toast.kind === "ok" && (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              )}
              {toast.kind === "error" && (
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              )}
              {toast.kind === "info" && (
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              )}
              <p className="flex-1 text-sm text-ink">{toast.message}</p>
              <button
                onClick={() => setToasts((t) => t.filter((x) => x.id !== toast.id))}
                className="text-muted hover:text-ink"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

/* --- Modal -------------------------------------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  // Escape must dismiss. A dialog you can only leave by finding the X is a
  // trap on a tablet, where the X is a small target and greasy hands miss it.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Portalled straight to <body>, deliberately outside Shell's animated
  // page-transition wrapper. `fixed` positioning is relative to the nearest
  // ancestor with a `transform` (or filter/perspective), not necessarily the
  // viewport — and Shell's route transition animates `y` on every page via
  // Framer Motion, which sets exactly that kind of transform on the wrapper
  // this modal would otherwise render inside. Rendered inline, a modal left
  // open while navigating away could get carried along in that wrapper's
  // exit animation and end up stuck covering the screen, invisibly blocking
  // clicks on whatever loaded underneath, with nothing in the console to
  // explain it. A portal makes the modal's DOM position independent of
  // wherever in the React tree it was opened from.
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION.fast }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={spring}
            className={cn(
              "relative w-full rounded-t-2xl bg-elevated shadow-lift sm:rounded-2xl",
              wide ? "sm:max-w-3xl" : "sm:max-w-lg",
            )}
          >
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="text-base font-semibold">{title}</h2>
              <button onClick={onClose} className="btn-ghost -mr-2 px-2" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/* --- Misc --------------------------------------------------------------- */

export function Chip({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "brand" | "ok" | "warn" | "danger";
}) {
  return (
    <span
      className={cn(
        "chip",
        tone === "neutral" && "bg-line/60 text-muted",
        tone === "brand" && "bg-brand/12 text-brand",
        tone === "ok" && "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
        tone === "warn" && "bg-amber-500/12 text-amber-600 dark:text-amber-400",
        tone === "danger" && "bg-red-500/12 text-red-600 dark:text-red-400",
      )}
    >
      {children}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="mb-6 flex flex-wrap items-start justify-between gap-4"
    >
      <div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </motion.div>
  );
}

export const List = motion.div;
export { staggerList, staggerItem, scaleIn, EASE };
